import { Button } from "@/components/ui/button";
import { ArrowLeft, Bot, LayoutDashboard, ListChecks } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface EventHeaderProps {
  isEditing: boolean;
  id: string;
  onBack: () => void;
  onEditToggle: () => void;
  onDashboard: () => void;
  onAiAssistant: () => void;
  status?: string;
  event: {
    title: string;
    date: string;
    end_date: string;
    description: string | null;
    location: string | null;
    category: string | null;
    expected_attendees: number | null;
  };
  activeView: 'details' | 'ai' | 'dashboard';
  hasUnsavedChanges: boolean;
  onSaveChanges: () => void;
  onCancelChanges: () => void;
}

export const EventHeader = ({
  isEditing,
  id,
  onBack,
  onEditToggle,
  onDashboard,
  onAiAssistant,
  activeView,
  hasUnsavedChanges,
  onSaveChanges,
  onCancelChanges
}: EventHeaderProps) => {
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<'dashboard' | 'ai' | 'back' | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleActionWithCheck = (action: 'dashboard' | 'ai' | 'back') => {
    if (isEditing && hasUnsavedChanges) {
      setPendingAction(action);
      setShowUnsavedDialog(true);
    } else {
      executeAction(action);
    }
  };

  const executeAction = (action: 'dashboard' | 'ai' | 'back') => {
    switch (action) {
      case 'dashboard':
        onDashboard();
        break;
      case 'ai':
        onAiAssistant();
        break;
      case 'back':
        onBack();
        break;
    }
  };

  const handleSaveAndContinue = () => {
    onSaveChanges();
    toast({
      description: "Changes saved successfully",
    });
    if (pendingAction) {
      executeAction(pendingAction);
      setPendingAction(null);
    }
    setShowUnsavedDialog(false);
  };

  const handleDiscardAndContinue = () => {
    onCancelChanges();
    toast({
      description: "Changes discarded",
    });
    if (pendingAction) {
      executeAction(pendingAction);
      setPendingAction(null);
    }
    setShowUnsavedDialog(false);
  };

  const handleManageEvent = () => {
    console.log("Navigating to manage event page with ID:", id);
    navigate(`/events/${id}/manage`);
  };

  return (
    <div className="space-y-4 mb-6">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button 
            variant="ghost" 
            onClick={() => handleActionWithCheck('back')}
            className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 p-2 -ml-2"
            size="icon"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <Button 
            variant={activeView === 'dashboard' ? "default" : "outline"} 
            onClick={() => handleActionWithCheck('dashboard')}
            className={activeView === 'dashboard' 
              ? "bg-[#8B5CF6] hover:bg-[#7C3AED]" 
              : "text-[#8B5CF6] border-[#8B5CF6] hover:bg-purple-50"}
          >
            <LayoutDashboard className="mr-2 h-4 w-4" />
            Dashboard
          </Button>
          
          <Button 
            variant={activeView === 'ai' ? "default" : "outline"} 
            onClick={() => handleActionWithCheck('ai')}
            className={activeView === 'ai' 
              ? "bg-[#8B5CF6] hover:bg-[#7C3AED]" 
              : "text-[#8B5CF6] border-[#8B5CF6] hover:bg-purple-50"}
          >
            <Bot className="mr-2 h-4 w-4" />
            AI Assistant
          </Button>
        </div>

        <div className="flex gap-2">
          <Button 
            onClick={handleManageEvent}
            className={activeView === 'dashboard' 
              ? "bg-[#8B5CF6] hover:bg-[#7C3AED]" 
              : "text-[#8B5CF6] border-[#8B5CF6] hover:bg-purple-50"}
            variant={activeView === 'dashboard' ? "default" : "outline"}
          >
            <ListChecks className="mr-2 h-4 w-4" />
            Manage Event
          </Button>
        </div>
      </div>

      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Would you like to save them before continuing?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDiscardAndContinue}>Discard Changes</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveAndContinue}>Save Changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
